import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { type ReactNode, type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { create } from "zustand";

import { api } from "../../api/client";
import type { Side } from "../../api/types";
import { motion, useLastDefined, usePresence } from "../../components/presence";
import { Kbd } from "../../components/ui";
import { lang, t, tServer } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { useManoeuvre } from "../manoeuvre/useManoeuvre";
import { FRIEND_BLUE } from "./symbols";
import {
  arrowGeometry,
  clampCenter,
  distanceM,
  formatDistance,
  formatDuration,
  formatRange,
  fractionBeforeL2,
  pathCoefficients,
  pathSamples,
  placeBubble,
  type Pt,
  ringLabelCenter,
  type Size,
  type Tone,
  travelHours,
  weatherTravelHours,
  unitInfo,
} from "./unitInfo";
import { useDeployment } from "./useDeployment";
import { weatherKey } from "../../weather";

/** Bulle d'information au survol d'une unité, anneau de portée de son arme principale, et mesure de distance et
 *  de temps depuis l'unité (iPad : survol et tapotement du crayon, `MapLibreMapView`). Le pointeur remplace le
 *  crayon : survol d'un symbole, touche M ou Alt + clic pour armer la mesure ; Échap, un clic, M, un
 *  déplacement de la carte ou 5 s sans mouvement pour la terminer. */

type LonLat = [number, number];

interface HoverState {
  /** Unité sous le pointeur (clé des déplacements). */
  hover: string | null;
  /** Mesure armée : unité de départ et point visé (null jusqu'au premier mouvement du pointeur, comme l'iPad). */
  measure: { key: string; to: LonLat | null } | null;
  setHover: (key: string | null) => void;
  arm: (key: string) => void;
  aim: (to: LonLat) => void;
  disarm: () => void;
  /** Déplacement de la carte, glisser-déposer, tracé, changement de temps : bulle et mesure retirées. */
  clear: () => void;
}

let clearTimer: number | undefined;
const AUTO_CLEAR_MS = 5_000;

export const useUnitHover = create<HoverState>((set, get) => ({
  hover: null,
  measure: null,
  setHover: (hover) => {
    if (get().hover !== hover) set({ hover });
  },
  arm: (key) => {
    window.clearTimeout(clearTimer);
    set({ measure: { key, to: null }, hover: key });
  },
  aim: (to) => {
    const measure = get().measure;
    if (measure) set({ measure: { ...measure, to } });
  },
  disarm: () => {
    window.clearTimeout(clearTimer);
    if (get().measure) set({ measure: null, hover: null });
  },
  clear: () => {
    window.clearTimeout(clearTimer);
    if (get().hover !== null || get().measure !== null) set({ hover: null, measure: null });
  },
}));

/** Couleur du camp sur la carte (anneau, flèche) : bleu ami et rouge ennemi de la symbologie OTAN. */
const SIDE_COLOR: Record<Side, string> = { AMI: FRIEND_BLUE, ENI: "#C83E3E", NEUTRE: "#15803D" };
/** Même couleur en texte sur la bulle claire (teintes lisibles de la charte). */
const SIDE_TEXT: Record<Side, string> = { AMI: "text-friend", ENI: "text-hostile", NEUTRE: "text-ok" };
const TONE_TEXT: Record<Tone, string> = { ok: "text-ok", warn: "text-warn", brand: "text-brand" };
const TONE_DOT: Record<Tone, string> = { ok: "bg-ok", warn: "bg-warn", brand: "bg-brand" };

/** Demi-hauteur utile d'un symbole d'unité : la bulle et la flèche dégagent le symbole. */
const SYMBOL_CLEARANCE = 26;

/** Projection de la carte (MapRef de react-map-gl, commune aux deux moteurs). */
export interface Projector {
  project: (lngLat: [number, number]) => { x: number; y: number };
  getContainer: () => HTMLElement;
}

/** Taille rendue d'un élément, relevée avant l'affichage (placement sans saut). */
function useSize<T extends HTMLElement>(): [RefObject<T | null>, Size] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<Size>({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setSize((s) => (Math.abs(s.w - w) < 0.5 && Math.abs(s.h - h) < 0.5 ? s : { w, h }));
  });
  return [ref, size];
}

interface Mobility {
  path: number;
  end: number;
  /** Moyenne par beau temps et majoration des ordres (null, 0 : serveur d'avant). */
  fair: number | null;
  delay: number;
  level: string;
  tone: Tone;
}

/** Coefficient de mobilité le long du trajet (moyenne de 5 points) et au point visé, demandé au serveur quand le
 *  pointeur s'est déplacé de plus de 12 px, une requête à la fois (comme le recalcul de l'iPad). */
function useMeasureMobility(from: LonLat | null, to: LonLat | null, toPx: Pt | null, enabled: boolean) {
  const mapSlug = useUi((s) => s.mapSlug);
  const params = useUi((s) => s.deploymentParams);
  const exerciseId = useUi((s) => s.exerciseId);
  const { data: exercises } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises?.find((e) => e.id === exerciseId);
  const [mobility, setMobility] = useState<Mobility | null>(null);
  const busy = useRef(false);
  const requested = useRef<Pt | null>(null);
  const alive = useRef(true);
  // Mesure réarmée (autre unité) ou terminée : les réponses de la précédente sont ignorées.
  const generation = useRef(0);
  const latest = useRef({ from, to, toPx });
  latest.current = { from, to, toPx };
  // Date, fuseau et météo du dossier affiché, brouillon des Données initiales compris (« auto » en CENTAC) ; le
  // dossier enregistré seulement sans dispositif publié, jamais champ par champ (une date effacée dans le brouillon
  // ne reprend pas celle du dossier).
  const shownWeather = useUi((s) => s.weatherKey);
  const weather = params
    ? { start_dtg: params.start_dtg, time_zone: params.time_zone, weather_scenario: shownWeather ?? weatherKey(exercise?.parameters) }
    : {
        start_dtg: exercise?.parameters.start_dtg ?? null,
        time_zone: exercise?.parameters.time_zone ?? null,
        weather_scenario: weatherKey(exercise?.parameters),
      };
  const weatherRef = useRef(weather);
  weatherRef.current = weather;

  const request = useCallback(() => {
    const { from, to, toPx } = latest.current;
    if (!enabled || !mapSlug || !from || !to || !toPx || busy.current) return;
    const last = requested.current;
    if (last && Math.hypot(toPx.x - last.x, toPx.y - last.y) <= 12) return;
    busy.current = true;
    requested.current = toPx;
    const sent = generation.current;
    api
      .mobility(mapSlug, { points: pathSamples(from, to), ...weatherRef.current })
      .then((r) => {
        const coefficients = pathCoefficients(r.coefficients, r.fair_coefficients);
        if (alive.current && coefficients && sent === generation.current) {
          setMobility({ ...coefficients, delay: r.delay_pct ?? 0, level: r.level, tone: r.tone });
        }
      })
      .catch(() => undefined)
      .finally(() => {
        busy.current = false;
        if (alive.current) request();
      });
  }, [enabled, mapSlug]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    generation.current += 1;
    requested.current = null;
    setMobility(null);
  }, [enabled, from?.[0], from?.[1]]);
  useEffect(request, [request, to?.[0], to?.[1]]);
  return enabled ? mobility : null;
}

export function UnitHoverOverlay({ mapRef }: { mapRef: RefObject<Projector | null> }) {
  const hover = useUnitHover((s) => s.hover);
  const measure = useUnitHover((s) => s.measure);
  const clear = useUnitHover((s) => s.clear);
  const phase = useUi((s) => s.phase);
  const opo = useUi((s) => s.opo);
  const { data: deployment } = useDeployment();
  const [bubbleRef, card] = useSize<HTMLDivElement>();
  const [ringRef, ringLabel] = useSize<HTMLDivElement>();
  const [pillRef, pill] = useSize<HTMLDivElement>();
  const [chipRef, chip] = useSize<HTMLDivElement>();

  // Les unités changent de place avec le temps ou l'OPO affiché : bulle et mesure retirées.
  useEffect(() => clear(), [phase, opo, clear]);

  // Clavier : M arme la mesure sur l'unité survolée (ou la termine), Échap la termine.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const state = useUnitHover.getState();
      if (e.key === "Escape" && state.measure) {
        // La mesure seule se termine : la fiche ouverte reste ouverte.
        e.preventDefault();
        e.stopImmediatePropagation();
        state.disarm();
        return;
      }
      if (e.key.toLowerCase() !== "m" || e.repeat) return;
      if (state.measure) {
        e.preventDefault();
        state.disarm();
      } else if (state.hover) {
        e.preventDefault();
        state.arm(state.hover);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  // Mode édition du fuseau : la bulle et son anneau de portée sont suspendus tant qu'une poignée est survolée ou
  // saisie (docs/14 §2.3) ; une mesure armée reste affichée.
  const suspended = useManoeuvre((s) => s.hover !== null || s.active !== null);
  // La bulle apparaît en léger zoom et s'efface en fondu : la dernière unité reste affichée pendant la sortie.
  const active = measure?.key ?? (suspended ? null : hover);
  const { mounted, closing } = usePresence(Boolean(active));
  const key = useLastDefined(active);
  const unit = key && mounted ? deployment?.units.find((u) => u.key === key) : undefined;
  const position = unit?.positions[phase];
  const map = mapRef.current;
  const from: LonLat | null = position ? [position.lon, position.lat] : null;
  const anchor = map && from ? map.project(from) : null;
  const to = measure?.to ?? null;
  const toPx = map && to ? map.project(to) : null;
  const info = unit ? unitInfo(unit, deployment?.placement?.strengths?.[unit.key]) : null;
  const arrow = anchor && toPx ? arrowGeometry(anchor, toPx, SYMBOL_CLEARANCE * 0.6) : null;
  // Mobilité demandée une fois la flèche tracée (pas au point de l'unité, à l'armement).
  const mobility = useMeasureMobility(
    from,
    arrow ? to : null,
    arrow ? toPx : null,
    Boolean(measure && info && !info.aerial),
  );

  // Effacement 5 s après le dernier mouvement de la flèche ; pas de flèche, pas d'effacement.
  const aimed = arrow ? `${to?.[0]},${to?.[1]}` : null;
  useEffect(() => {
    window.clearTimeout(clearTimer);
    if (aimed) clearTimer = window.setTimeout(() => useUnitHover.getState().disarm(), AUTO_CLEAR_MS);
  }, [aimed]);

  if (!map || !unit || !info || !anchor || !from) return null;
  const leaving = closing || !active;
  const container = map.getContainer();
  const frame: Size = { w: container.clientWidth, h: container.clientHeight };
  const side = unit.side;
  const color = SIDE_COLOR[side];

  const placed = placeBubble(anchor, card, frame, SYMBOL_CLEARANCE, arrow && toPx ? toPx.y >= anchor.y : undefined);
  const bubbleBox = { x: placed.x, y: placed.y, w: card.w, h: card.h };

  // Anneau de portée (survol simple) : tracé seulement si son rayon à l'écran est exploitable.
  let ring: { r: number; label: Pt } | null = null;
  if (!measure && !leaving && info.range) {
    const north = map.project([from[0], from[1] + info.range.m / 111_320]);
    const r = Math.hypot(north.x - anchor.x, north.y - anchor.y);
    if (r >= 18 && r <= 1.6 * Math.max(frame.w, frame.h)) {
      ring = { r, label: ringLabelCenter(anchor, r, ringLabel, frame, bubbleBox) };
    }
  }

  // Mesure : distance à vol d'oiseau et temps de trajet (allure par bande de LIMA, mobilité du terrain).
  let measured: { text: string; pill: Pt; chip: Pt | null } | null = null;
  if (measure && !leaving && arrow && to) {
    const meters = distanceM(from, to);
    const fraction = fractionBeforeL2(deployment?.corridor, from, to);
    // Au sol : terrain par beau temps majoré de la ligne « Délais » des ordres (météo comptée une seule fois).
    const hours = info.aerial
      ? travelHours(meters / 1000, info.speedKmh, 1, fraction)
      : mobility
        ? weatherTravelHours(meters / 1000, info.speedKmh, mobility, mobility.delay, fraction)
        : null;
    const text = hours === null ? formatDistance(meters) : `${formatDistance(meters)} · ${formatDuration(hours)}`;
    const pillAt = clampCenter(arrow.label, pill, frame);
    let chipAt: Pt | null = null;
    if (!info.aerial && mobility) {
      // Sous la pastille (6 px d'écart), au-dessus faute de place.
      const below = pillAt.y + pill.h / 2 + 6 + chip.h / 2;
      const above = pillAt.y - pill.h / 2 - 6 - chip.h / 2;
      chipAt = clampCenter({ x: pillAt.x, y: below <= frame.h - chip.h / 2 - 6 ? below : above }, chip, frame);
    }
    measured = { text, pill: pillAt, chip: chipAt };
  }

  return (
    <div className="pointer-events-none absolute inset-0 z-[5] overflow-hidden">
      <svg className="absolute inset-0 size-full" aria-hidden>
        {ring && (
          <circle
            className="gx-enter-fade"
            cx={anchor.x}
            cy={anchor.y}
            r={ring.r}
            fill={color}
            fillOpacity={0.06}
            stroke={color}
            strokeWidth={1.6}
          />
        )}
        {measure && !leaving && arrow && (
          <g className="gx-enter-fade">
            {/* Liseré blanc sous chaque tiret et sous la pointe : flèche lisible sur le plan comme sur l'ortho. */}
            <line x1={arrow.start.x} y1={arrow.start.y} x2={arrow.end.x} y2={arrow.end.y} stroke="#fff"
              strokeOpacity={0.9} strokeWidth={5} strokeDasharray="5 6" strokeLinecap="round" />
            <line x1={arrow.start.x} y1={arrow.start.y} x2={arrow.end.x} y2={arrow.end.y} stroke={color}
              strokeWidth={2.5} strokeDasharray="5 6" strokeLinecap="round" />
            <polygon points={arrow.head.map((p) => `${p.x},${p.y}`).join(" ")} fill={color} stroke="#fff"
              strokeOpacity={0.9} strokeWidth={1.2} strokeLinejoin="round" />
          </g>
        )}
      </svg>

      {ring && info.range && (
        <Floating ref={ringRef} at={ring.label} size={ringLabel}>
          <span className={clsx("font-semibold", SIDE_TEXT[side])}>{t("Portée {range}", { range: formatRange(info.range.m) })}</span>
        </Floating>
      )}

      {measured && (
        <Floating ref={pillRef} at={measured.pill} size={pill}>
          <span className="font-semibold text-ink tabular-nums">{measured.text}</span>
        </Floating>
      )}
      {measured?.chip && mobility && (
        <Floating ref={chipRef} at={measured.chip} size={chip}>
          <span className="flex items-center gap-1.5">
            <span className={clsx("size-[7px] rounded-full", TONE_DOT[mobility.tone])} />
            <span className={clsx("font-bold tabular-nums", TONE_TEXT[mobility.tone])}>
              {lang() === "en" ? mobility.end.toFixed(2) : mobility.end.toFixed(2).replace(".", ",")}
            </span>
            <span className="text-[11px] font-medium text-ink-soft">{tServer(mobility.level)}</span>
          </span>
        </Floating>
      )}

      <div
        ref={bubbleRef}
        className={clsx(
          "absolute min-w-[168px] max-w-[320px] rounded-xl border border-line bg-surface/95 px-3 pt-2.5 pb-2 shadow-float backdrop-blur",
          motion("zoom", leaving),
        )}
        style={{ left: placed.x, top: placed.y, visibility: card.w ? "visible" : "hidden" }}
      >
        <Caret above={placed.above} x={placed.caretX} />
        <div className="truncate text-[13px] font-semibold text-ink">{info.title}</div>
        <dl className="mt-1.5 space-y-1">
          <Row label={t("Matériel majeur")} value={info.materiel} />
          <Row label={t("Quantité")} value={info.quantity} />
          <Row label={t("Potentiel")} value={info.potential.text} className={TONE_TEXT[info.potential.tone]} />
          <Row label={t("Portée")} value={info.range?.text ?? null} className={SIDE_TEXT[side]} />
        </dl>
        <div className="mt-2 flex items-center gap-1.5 border-t border-line pt-1.5 text-[10.5px] text-ink-muted">
          {measure ? (
            <>
              <Kbd>{t("Échap")}</Kbd> {t("terminer la mesure")}
            </>
          ) : (
            <>
              <Kbd>M</Kbd> {t("mesurer distance et temps")}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, className }: { label: string; value: string | null; className?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-[11px] text-ink-muted">{label}</dt>
      <dd
        className={clsx("min-w-0 truncate text-right text-[12.5px] font-semibold", value ? (className ?? "text-ink") : "text-ink-muted")}
        title={value ?? undefined}
      >
        {value ?? "—"}
      </dd>
    </div>
  );
}

/** Pointe de la bulle vers l'unité (carré tourné, bordé comme la carte). */
function Caret({ above, x }: { above: boolean; x: number }) {
  return (
    <span
      className={clsx(
        "absolute size-3 rotate-45 border-line bg-surface",
        above ? "-bottom-[6.5px] border-r border-b" : "-top-[6.5px] border-t border-l",
      )}
      style={{ left: x - 6 }}
    />
  );
}

/** Pastille posée par son centre (portée, distance et temps, mobilité), tenue dans la carte. */
function Floating({ ref, at, size, children }: { ref: RefObject<HTMLDivElement | null>; at: Pt; size: Size; children: ReactNode }) {
  return (
    <div
      ref={ref}
      className="gx-enter-fade absolute rounded-lg border border-line bg-surface/95 px-2 py-1 text-[12px] whitespace-nowrap shadow-float backdrop-blur"
      style={{ left: at.x - size.w / 2, top: at.y - size.h / 2, visibility: size.w ? "visible" : "hidden" }}
    >
      {children}
    </div>
  );
}
